/**
 * PremiumSection — the wrapper every storefront block passes through.
 *
 * This is the single component that makes Entreprenatives look like
 * Squarespace/Wix/Framer. It reads a `block.layout` object and applies:
 *
 *   • container_width: narrow (720) | content (1140) | wide (1440) | full-bleed (100vw)
 *   • padding_scale:   compact | normal | editorial (140/80) | cinematic (200/120)
 *   • background_variant: light | cream | noir | tinted | accent (alternating)
 *   • full_bleed_media: images/hero backgrounds run edge-to-edge
 *   • aspect_ratio:    1:1, 4:5, 3:4, 16:9, 21:9
 *
 * Defaults are Squarespace-tier: `content` width, `editorial` padding.
 */
import React from 'react';

export const CONTAINER_WIDTHS = {
  narrow:      { max: 720,  label: 'Narrow',    hint: 'Long-form text' },
  content:     { max: 1140, label: 'Content',   hint: 'Standard' },
  wide:        { max: 1440, label: 'Wide',      hint: 'Galleries' },
  'full-bleed':{ max: null, label: 'Full-bleed',hint: 'Edge-to-edge' },
};

export const PADDING_SCALES = {
  compact:   { pyDesktop: 48,  pyMobile: 32,  label: 'Compact'    },
  normal:    { pyDesktop: 88,  pyMobile: 56,  label: 'Normal'     },
  editorial: { pyDesktop: 140, pyMobile: 80,  label: 'Editorial'  },
  cinematic: { pyDesktop: 200, pyMobile: 120, label: 'Cinematic'  },
};

export const ASPECT_RATIOS = {
  auto:  { ratio: null,    label: 'Auto (natural)' },
  '1:1': { ratio: '1 / 1', label: 'Square (1:1)' },
  '4:5': { ratio: '4 / 5', label: 'Portrait (4:5)' },
  '3:4': { ratio: '3 / 4', label: 'Tall (3:4)' },
  '16:9':{ ratio: '16 / 9', label: 'Landscape (16:9)' },
  '21:9':{ ratio: '21 / 9', label: 'Cinema (21:9)' },
};

export const BG_VARIANTS = {
  auto:   null,  // triggers alternating bands
  light:  { bg: '#FFFFFF', fg: '#0E0E23' },
  cream:  { bg: '#FAF6EE', fg: '#0E0E23' },
  ivory:  { bg: '#F5F0E8', fg: '#0E0E23' },
  tinted: { bg: '#F8FAFC', fg: '#0E0E23' },
  noir:   { bg: '#0E0E23', fg: '#F5F0E8' },
  accent: { bg: null, fg: '#FFFFFF' },  // uses site accent color
};

const ALTERNATING_BANDS = ['light', 'cream', 'light', 'ivory'];  // 4-step rhythm

/**
 * Get the resolved background variant for a block at a given index in the
 * page. Respects an explicit `layout.background_variant`; otherwise, when
 * `alternating_bands` is enabled on the site theme, cycles through a
 * 4-step rhythm.
 */
export function resolveBackground({ block, index = 0, siteTheme = {}, accent = '#8B5CF6' }) {
  const layout = block.layout || {};
  const chosen = layout.background_variant;
  if (chosen && chosen !== 'auto' && BG_VARIANTS[chosen]) {
    const v = BG_VARIANTS[chosen];
    if (chosen === 'accent') return { bg: accent, fg: '#FFFFFF' };
    return v;
  }
  // Auto — alternate bands if enabled
  if (siteTheme.alternating_bands) {
    const key = ALTERNATING_BANDS[index % ALTERNATING_BANDS.length];
    return BG_VARIANTS[key];
  }
  // Fall back to legacy block style
  const s = block.style || {};
  const c = block.content || {};
  return {
    bg: s.bg_color || c.bg_color || null,
    fg: s.fg_color || c.fg_color || null,
  };
}

/**
 * PremiumSection — wraps any block. `mediaBleed` prop toggles whether the
 * section becomes truly full-viewport (used by hero + image blocks).
 */
export default function PremiumSection({
  block,
  index = 0,
  siteTheme = {},
  accent = '#8B5CF6',
  children,
  mediaBleed: mediaBleedOverride,
  className = '',
  style: styleOverride = {},
  ...rest
}) {
  const layout = block.layout || {};
  const width = CONTAINER_WIDTHS[layout.container_width] || CONTAINER_WIDTHS.content;
  const padding = PADDING_SCALES[layout.padding_scale] || PADDING_SCALES.editorial;
  const { bg, fg } = resolveBackground({ block, index, siteTheme, accent });

  const isFullBleed = layout.container_width === 'full-bleed' || !!(mediaBleedOverride ?? layout.full_bleed_media);

  const sectionStyle = {
    backgroundColor: bg || undefined,
    color: fg || undefined,
    paddingTop: `${padding.pyDesktop}px`,
    paddingBottom: `${padding.pyDesktop}px`,
    ...styleOverride,
  };

  return (
    <section
      className={`wb-premium-section ${className}`}
      style={sectionStyle}
      data-testid={`premium-section-${block.type}-${block.id}`}
      data-width={layout.container_width || 'content'}
      data-padding={layout.padding_scale || 'editorial'}
      {...rest}
    >
      <div
        className="mx-auto px-6 sm:px-8"
        style={{ maxWidth: isFullBleed ? '100%' : `${width.max || 1140}px` }}
      >
        {children}
      </div>
      <style>{`
        @media (max-width: 640px) {
          .wb-premium-section[data-padding="editorial"] { padding-top: ${padding.pyMobile}px !important; padding-bottom: ${padding.pyMobile}px !important; }
          .wb-premium-section[data-padding="cinematic"] { padding-top: ${padding.pyMobile}px !important; padding-bottom: ${padding.pyMobile}px !important; }
        }
      `}</style>
    </section>
  );
}
